// Employee Tracking Policy strings — EN + TR.
//
// Wave-2 Day 2. Admin-only Settings section governing company-wide
// screenshot capture + idle detection policy (M3 backend syncs to Cattr API
// per user). 209 LOC source file was TR-heavy with 4 EN headers per i18n
// audit 2026-06-02 (commit ca624ba).
//
// Source: features/settings/EmployeeTracking.tsx.

import type { LocalizedDict } from '../index'

type EmployeeTrackingStrings = {
  pageTitle: string
  pageDescription: string
  adminOnly: string
  cards: {
    screenshot: {
      title: string
      description: string
      intervalLabel: string
      intervalHint: string
    }
    idle: {
      title: string
      description: string
      inactivityLabel: string
      inactivityHint: string
    }
  }
  intervalOptions: {
    one: string
    five: string
    ten: string
    fifteen: string
    thirty: string
  }
  inactivityOptions: {
    sixty: string
    oneEighty: string
    threeHundred: string
    sixHundred: string
    twelveHundred: string
  }
  impactLabel: string
  impactCount: (count: number) => string
  impactTail: string
  save: string
  saved: string
  toast: {
    title: string
    description: (count: number) => string
  }
  simulationNote: {
    badge: string
    body: string
    backendNote: string
  }
}

export const employeeTrackingI18n: LocalizedDict<EmployeeTrackingStrings> = {
  en: {
    pageTitle: 'Employee Tracking Policy',
    pageDescription:
      'Company-wide screenshot capture and idle detection settings. Applied automatically to every employee. Employees cannot modify these on their own machines.',
    adminOnly: 'Employee Tracking Policy is admin-only.',
    cards: {
      screenshot: {
        title: 'Screenshot capture',
        description:
          'How often a screenshot is taken from the employee desktop.',
        intervalLabel: 'Screenshot interval',
        intervalHint: 'A shorter interval consumes more storage',
      },
      idle: {
        title: 'Idle detection',
        description:
          'How long the desktop stays idle before tracking is paused.',
        inactivityLabel: 'Inactivity timeout',
        inactivityHint:
          'If there is no mouse or keyboard activity for this period, the timer auto-pauses',
      },
    },
    intervalOptions: {
      one: '1 minute (heavy monitoring)',
      five: '5 minutes',
      ten: '10 minutes (recommended)',
      fifteen: '15 minutes',
      thirty: '30 minutes (light monitoring)',
    },
    inactivityOptions: {
      sixty: '1 minute',
      oneEighty: '3 minutes',
      threeHundred: '5 minutes (recommended)',
      sixHundred: '10 minutes',
      twelveHundred: '20 minutes',
    },
    impactLabel: 'Impact:',
    impactCount: (count) => `${count} employee${count === 1 ? '' : 's'}`,
    impactTail: '(new joiners start with this policy automatically)',
    save: 'Save Policy',
    saved: 'Saved',
    toast: {
      title: 'Policy saved (mock)',
      description: (count) =>
        `${count} employee${count === 1 ? '' : 's'} will be synced — M3 backend integration pending.`,
    },
    simulationNote: {
      badge: 'M0 simulation note:',
      body: 'This form is currently a mock — values are stored in PEXIFY frontend state and do not propagate to the Cattr DB.',
      backendNote:
        'M3 Stage 4 SPEC.md Faz 5 (Notifications + Policies + Routing) wires the backend TrackingPolicyService to PATCH every employee record via the Cattr API + re-syncs periodically to override local edits.',
    },
  },
  tr: {
    pageTitle: 'Çalışan İzleme Politikası',
    pageDescription:
      'Şirket geneli ekran görüntüsü ve boşta kalma algılama ayarları. Her çalışana otomatik uygulanır. Çalışanlar kendi makinelerinde bu ayarları değiştiremez.',
    adminOnly: 'Çalışan İzleme Politikası yalnızca admin\'e açıktır.',
    cards: {
      screenshot: {
        title: 'Ekran görüntüsü yakalama',
        description:
          'Çalışan masaüstünden ne sıklıkta ekran görüntüsü alınacak.',
        intervalLabel: 'Ekran görüntüsü aralığı',
        intervalHint: 'Daha sık aralık daha fazla depolama tüketir',
      },
      idle: {
        title: 'Boşta kalma algılama',
        description:
          'Bilgisayar hareketsiz kaldığında ne kadar sonra takip duraklasın.',
        inactivityLabel: 'Hareketsizlik zaman aşımı',
        inactivityHint:
          'Bu süre boyunca fare + klavye hareketi yoksa sayaç otomatik duraklar',
      },
    },
    intervalOptions: {
      one: '1 dakika (yoğun izleme)',
      five: '5 dakika',
      ten: '10 dakika (önerilen)',
      fifteen: '15 dakika',
      thirty: '30 dakika (hafif izleme)',
    },
    inactivityOptions: {
      sixty: '1 dakika',
      oneEighty: '3 dakika',
      threeHundred: '5 dakika (önerilen)',
      sixHundred: '10 dakika',
      twelveHundred: '20 dakika',
    },
    impactLabel: 'Etki:',
    impactCount: (count) => `${count} çalışan`,
    impactTail: '(yeni katılanlar otomatik bu politika ile başlar)',
    save: 'Politikayı Kaydet',
    saved: 'Kaydedildi',
    toast: {
      title: 'Politika kaydedildi (mock)',
      description: (count) =>
        `${count} çalışana senkronize edilecek — M3 backend entegrasyonu beklemede.`,
    },
    simulationNote: {
      badge: 'M0 simülasyon notu:',
      body: 'Bu form şu an mock — değer PEXIFY önyüz durumunda tutulur, gerçek Cattr DB\'sine yansımaz.',
      backendNote:
        'M3 Stage 4 SPEC.md Faz 5 (Notifications + Policies + Routing) ile backend TrackingPolicyService Cattr API üzerinden tüm çalışan kayıtlarına PATCH atacak + periyodik yeniden senkronizasyon ile çalışan tarafından yapılan yerel değişikliklerin üzerine yazacak.',
    },
  },
}
